import { ITimeTablePeriod } from "./appointment.interface";

/** Shared by the regular and the package booking screens (both mobile specs, §5.1 / §4.1). */

export type BookingLang = "vi" | "en" | "jp";

/**
 * Doctors, services, sites, promotions and fees answer in the language of the PATIENT, not of the
 * UI: a foreign patient (`patient_ethic = "55"`) gets English, everyone else Vietnamese.
 */
export const apiLangForRecord = (record?: { patient_ethic?: string | null } | null, uiLang?: string): BookingLang => {
  if ((record?.patient_ethic ?? "").trim() !== "55") {
    return "vi";
  }

  return uiLang === "jp" ? "jp" : "en";
};

const VN_OFFSET_MS = 7 * 3600 * 1000;

const toEpoch = (value: unknown) => {
  if (typeof value === "number") {
    return value;
  }

  const parsed = typeof value === "string" && /^\d+$/.test(value) ? Number(value) : Date.parse(String(value ?? ""));

  return Number.isFinite(parsed) ? parsed : NaN;
};

/** The Vietnam day `yyyy-MM-dd` an instant falls on. */
const vnYmd = (epoch: number) => new Date(epoch + VN_OFFSET_MS).toISOString().slice(0, 10);

/** Hour of day on Vietnam time. */
export const vnHour = (value: unknown) => {
  const epoch = toEpoch(value);

  return Number.isFinite(epoch) ? new Date(epoch + VN_OFFSET_MS).getUTCHours() : NaN;
};

/**
 * The slots a patient may still pick on `date`: none that already started, and - on the day the
 * package or use lapses - none that start after it does.
 */
export const bookableSlots = (slots: ITimeTablePeriod[], date: string, now = Date.now(), cutoff?: Date | null) => {
  const isToday = vnYmd(now) === date;
  const cutoffAt = cutoff ? +cutoff : NaN;
  const isCutoffDay = Number.isFinite(cutoffAt) && vnYmd(cutoffAt) === date;

  return slots.filter((slot) => {
    const start = toEpoch(slot.period_start_time);

    if (!Number.isFinite(start)) {
      return false;
    }

    if (isToday && start <= now) {
      return false;
    }

    return !(isCutoffDay && start > cutoffAt);
  });
};

/** `300.000đ`: dot thousands, no decimals, `đ` suffix. */
export const formatVnd = (amount?: number | null) =>
  `${Math.round(Number(amount ?? 0))
    .toLocaleString("vi-VN")
    .replace(/,/g, ".")}đ`;

export interface ISymptomSetting {
  symptom_name?: string;
  description?: string;
  specialization?: { specialization_code?: string };
}

/** Suggested symptoms for the service's specialty; no specialty shows them all. */
export const symptomsForSpecialization = (symptoms: ISymptomSetting[], specializationCode?: string | null) => {
  const code = (specializationCode ?? "").trim();

  return code ? symptoms.filter((symptom) => symptom.specialization?.specialization_code === code) : symptoms;
};

const SEPARATOR = ", ";

/** A chip adds its text to the symptoms (joined by ", ") or, when already there, takes exactly that part out. */
export const toggleSymptomText = (current: string, text: string) => {
  const piece = text.trim();

  if (!piece) {
    return current;
  }

  const parts = current
    .split(SEPARATOR)
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.includes(piece)) {
    return parts.filter((part) => part !== piece).join(SEPARATOR);
  }

  return [...parts, piece].join(SEPARATOR);
};

export const hasSymptom = (current: string, text: string) =>
  current
    .split(SEPARATOR)
    .map((part) => part.trim())
    .includes(text.trim());
